import {assertCondition, isEqual, isNil} from "app/utils/stdlib";
import {useControlCardInstanceContext} from "app/employee/controlCard/ControlCardInstanceContext";
import {useCallback, useMemo} from "react";
import {
  ControlCardInstanceAddPrimaryKeyRowsDocument,
  type ControlCardInstanceIdFragment,
  type ValueFragment
} from "app/gql/graphqlSchema";
import {query} from "app/gql/client";
import {linkEmplControlCardInstance} from "app/employee/core/employeeLinks";
import {useIonRouter} from "@ionic/react";
import {PrimaryRowTextValue} from "./PrimaryRowTextValue";
import {PrimaryRowComboBox} from "app/employee/controlCard/primaryKey/PrimaryRowComboBox";
import {useLoadingContext} from "app/employee/LoadingContext";
import {PrimaryRowEmployeeRefValue} from "app/employee/controlCard/primaryKey/PrimaryRowEmployeeRefValue";
import type {PrimaryRowValue} from "app/employee/controlCard/primaryKey/types";

const addRows = async (instanceId: ControlCardInstanceIdFragment, value: ValueFragment[]) => {
  assertCondition(value.length > 0);
  const result = await query(ControlCardInstanceAddPrimaryKeyRowsDocument, {
    input: {instanceId, primaryKeyValues: JSON.stringify(value)}
  });
  const newRows = result.controlCardInstanceAddPrimaryKeyRows.rows;
  assertCondition(newRows.length > 0);
  return newRows.map((r) => r.rowId);
};

export const AddPrimaryKeyRow = () => {
  const {instance, primaryColumn} = useControlCardInstanceContext();
  const {startLoading, finishLoading} = useLoadingContext();
  const {push} = useIonRouter();

  const setValues = useCallback(
    async (values: ValueFragment[]) => {
      startLoading();
      try {
        const newRowIds = await addRows(instance.id, values);
        if (newRowIds.length >= 0) {
          push(linkEmplControlCardInstance(instance.id.instanceId, newRowIds[0]), "forward");
        } else {
          push(linkEmplControlCardInstance(instance.id.instanceId), "back");
        }
      } finally {
        finishLoading();
      }
    },
    [finishLoading, instance.id, push, startLoading]
  );

  const primaryRowValues = useMemo(() => {
    const selectedValues: PrimaryRowValue[] = [];
    for (const row of instance.rows) {
      const column = row.columns.find((c) => isEqual(c.columnKey, primaryColumn.key));
      if (!isNil(column)) {
        selectedValues.push({rowId: row.rowId, value: column.value});
      }
    }

    return selectedValues;
  }, [instance.rows, primaryColumn.key]);

  if (primaryColumn?.__typename === "ControlCardEmployeeRefColumn") {
    return <PrimaryRowEmployeeRefValue column={primaryColumn} setValues={setValues} values={primaryRowValues} />;
  } else if (primaryColumn?.__typename === "ControlCardTextColumn") {
    return <PrimaryRowTextValue column={primaryColumn} setValues={setValues} />;
  } else if (primaryColumn?.__typename === "ControlCardComboBoxColumn") {
    return <PrimaryRowComboBox column={primaryColumn} setValues={setValues} values={primaryRowValues} />;
  } else {
    return null;
  }
};
